/**
 * "Documents Reviewed" apply gate — shared by the job apply UI and the applyToJob Cloud Function
 * (synced verbatim into functions/src/generated/ by scripts/syncFunctionsGenerated.mjs), so the
 * client explanation and the server enforcement can never disagree.
 *
 * "Documents Reviewed" means a certification's uploaded document passed the automated name /
 * date-of-birth / expiration / genuine-document checks (verifyCredentialDocument → trusted
 * profiles/{uid}.certificationChecks, status "under_review"). It is NOT license verification against
 * NREMT or a state board — never label it "Verified".
 *
 * Gate:
 *  - Job lists required certifications → EVERY required certification (alias-aware: EMT-B ≡ NREMT-B)
 *    must have a profile entry whose document is reviewed. Matching is job-aware
 *    (certificationSatisfiesForJob): a required State EMS Certification needs one for the job's
 *    state, and in a state that certifies without NREMT (and the job does not require NREMT) a
 *    reviewed State EMS Certification for that state meets an NREMT-level requirement.
 *  - Job lists none → nothing to review: the candidate may apply with no reviewed document at all.
 *    (Until 2026-10-05 such a job demanded "at least one" reviewed certification of any kind, which
 *    blocked a driver with no certification from a job that asked for none. Founder decision: only
 *    the credentials the job actually lists are gated.)
 */
import type { AiVerification } from '../../types/aiVerification';
import { resolveCertVerification } from './certificationChecks';
import {
  certificationMatchKey,
  certificationSatisfiesForJob,
  normalizeStateCertificationLevel,
  describeCertificationRequirement,
  displayCertificationName,
  normalizeStateCode,
  STATE_EMS_CERTIFICATION,
} from '../constants/certifications';
import { certificationJobContext, getJobCertRequirements } from '../certificationMatching';
import { isExpired, scoringClock } from '../readiness/certUtils';

/** The one status that means "passed the automated document checks". */
export const DOCUMENTS_REVIEWED_STATUS = 'under_review';
export const DOCUMENTS_REVIEWED_LABEL = 'Documents Reviewed';
/** Prefix of every gate rejection message (client uses it to recognise a gate block). */
export const DOCUMENTS_REVIEWED_REQUIRED_PREFIX = 'Documents Reviewed required';

export function isDocumentReviewed(verification: AiVerification | null | undefined): boolean {
  return verification?.status === DOCUMENTS_REVIEWED_STATUS;
}

export type DocumentReviewGapReason =
  | 'not_on_profile'
  | 'expired'
  | 'no_document'
  | 'checking'
  | 'rejected'
  | 'check_unavailable'
  | 'not_checked';

export type DocumentReviewGap = {
  /** Display name of the required certification (canonical: also the name an upload is saved under). */
  certification: string | null;
  /**
   * How the requirement is worded for this job's candidates (describeCertificationRequirement), e.g.
   * "EMT-B (state certification or NREMT)" in Florida or "State EMS Certification (FL)". Same as
   * `certification` when no state rule applies. Optional so older callers that build gaps keep compiling.
   */
  label?: string | null;
  /** For a required State EMS Certification: the job's state, so an upload can record it. */
  state?: string | null;
  reason: DocumentReviewGapReason;
  /** Candidate-facing rejection reason from the document check, when rejected. */
  detail: string | null;
};

export type DocumentReviewGate = {
  eligible: boolean;
  /** 'no_required_certifications': the job lists none, so the gate is always open. */
  mode: 'required_certifications' | 'no_required_certifications';
  requiredCertifications: string[];
  /** requiredCertifications as worded for this job's candidates (same order). Optional for hand-built gates. */
  requiredCertificationLabels?: string[];
  /** Profile certifications (display names) whose documents are reviewed. */
  reviewedCertifications: string[];
  gaps: DocumentReviewGap[];
};

type CertEntry = { name: string; state: string | null; level: string | null; raw: Record<string, unknown> };

function profileCertEntries(profile: unknown): CertEntry[] {
  const certs = (profile as { professional?: { certifications?: unknown } } | null | undefined)?.professional?.certifications;
  if (!Array.isArray(certs)) return [];
  return certs
    .filter((c): c is Record<string, unknown> => Boolean(c) && typeof c === 'object')
    .map((c) => ({ name: typeof c.name === 'string' ? c.name.trim() : '', state: normalizeStateCode(c.state), level: normalizeStateCertificationLevel(c.level), raw: c }))
    .filter((c) => c.name.length > 0);
}

// Most-actionable-first when several profile entries match one requirement.
const REASON_RANK: Record<Exclude<DocumentReviewGapReason, 'not_on_profile'>, number> = {
  expired: -1,
  checking: 0,
  rejected: 1,
  check_unavailable: 2,
  not_checked: 3,
  no_document: 4,
};

/** The expiry date typed on a profile certification entry (same fields extractCertifications reads). */
function entryExpiry(entry: CertEntry): string | undefined {
  const raw = entry.raw as { expiryDate?: unknown; expiresAt?: unknown };
  const v = raw.expiryDate || raw.expiresAt;
  return typeof v === 'string' && v ? v : undefined;
}

function entryGap(entry: CertEntry, checks: unknown, now: Date): { reviewed: true } | { reviewed: false; reason: keyof typeof REASON_RANK; detail: string | null } {
  const verification = resolveCertVerification(entry.raw, checks);
  // Reviewed AND current (the counting rule, certUtils certCounts): a reviewed card that has since
  // expired no longer meets a requirement (2026-10-13; before, it opened applying forever).
  if (isDocumentReviewed(verification)) {
    return isExpired(entryExpiry(entry), now) ? { reviewed: false, reason: 'expired', detail: null } : { reviewed: true };
  }
  const hasFile = typeof entry.raw.fileUrl === 'string' && entry.raw.fileUrl.length > 0;
  if (!hasFile) return { reviewed: false, reason: 'no_document', detail: null };
  switch (verification?.status) {
    case 'pending':
      return { reviewed: false, reason: 'checking', detail: null };
    case 'rejected':
      return { reviewed: false, reason: 'rejected', detail: verification.reasonMessage ?? null };
    case 'unavailable':
      return { reviewed: false, reason: 'check_unavailable', detail: null };
    default:
      return { reviewed: false, reason: 'not_checked', detail: null };
  }
}

/**
 * Every certification on a profile, split by whether its document actually passed the automated
 * checks. The ONE place that answer is computed — the apply gate above, and the AI resume
 * generator (functions/src/generateEmtResume.ts, via the synced copy of this file), must never
 * disagree about whether a candidate holds a credential.
 *
 * Status comes from the trusted certificationChecks map through resolveCertVerification, so a
 * client-written "under_review" or "verified" on the certification entry is ignored.
 */
export type ClassifiedCertifications = {
  /** Documents passed: safe to state as fact. */
  reviewed: Array<{ name: string; expiresAt: string | null }>;
  /** Claimed but not established — must not be stated as fact anywhere. */
  excluded: Array<{ name: string; reason: Exclude<DocumentReviewGapReason, 'not_on_profile'>; detail: string | null }>;
};

export function classifyProfileCertifications(profile: unknown, nowIn: Date = new Date()): ClassifiedCertifications {
  const checks = (profile as { certificationChecks?: unknown } | null | undefined)?.certificationChecks;
  const now = scoringClock(profile, nowIn);
  const reviewed: ClassifiedCertifications['reviewed'] = [];
  const excluded: ClassifiedCertifications['excluded'] = [];
  for (const entry of profileCertEntries(profile)) {
    const result = entryGap(entry, checks, now);
    const display = displayCertificationName(entry.name);
    if (result.reviewed) {
      const raw = entry.raw as { expiresAt?: unknown };
      reviewed.push({ name: display, expiresAt: typeof raw.expiresAt === 'string' && raw.expiresAt ? raw.expiresAt : null });
    } else {
      excluded.push({ name: display, reason: result.reason, detail: result.detail });
    }
  }
  return { reviewed, excluded };
}

export function getDocumentReviewGate(params: {
  profile: unknown;
  job: Parameters<typeof getJobCertRequirements>[0] | null | undefined;
  /** "Now"; read on the EMT's own calendar (certUtils scoringClock). Defaults to the current time. */
  now?: Date;
}): DocumentReviewGate {
  const checks = (params.profile as { certificationChecks?: unknown } | null | undefined)?.certificationChecks;
  const now = scoringClock(params.profile, params.now ?? new Date());
  const entries = profileCertEntries(params.profile);
  const reviewedEntries = entries.filter((e) => entryGap(e, checks, now).reviewed);
  const reviewedCertifications = [...new Set(reviewedEntries.map((e) => displayCertificationName(e.name)))];

  const required = params.job ? getJobCertRequirements(params.job).required : [];
  const ctx = params.job ? certificationJobContext(params.job) : null;
  // One requirement per credential (EMT-B and NREMT-B listed together are one requirement).
  const seen = new Set<string>();
  const requirements = required.filter((r) => {
    const k = certificationMatchKey(r);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  if (requirements.length === 0) {
    // Nothing listed, nothing to review: no document is asked for (founder decision 2026-10-05).
    return {
      eligible: true,
      mode: 'no_required_certifications',
      requiredCertifications: [],
      requiredCertificationLabels: [],
      reviewedCertifications,
      gaps: [],
    };
  }

  const gaps: DocumentReviewGap[] = [];
  for (const req of requirements) {
    // Same credential or a higher EMS level: a Paramedic's reviewed NREMT-P document satisfies an
    // EMT-B requirement, exactly as it does in job matching (certificationSatisfies).
    const matching = entries.filter((e) => certificationSatisfiesForJob(e.name, e.state, req, ctx, e.level));
    const display = displayCertificationName(req);
    const label = describeCertificationRequirement(req, ctx);
    const state = display === STATE_EMS_CERTIFICATION ? normalizeStateCode(ctx?.jobState) : null;
    const where = { certification: display, label, ...(state ? { state } : {}) };
    if (matching.length === 0) {
      gaps.push({ ...where, reason: 'not_on_profile', detail: null });
      continue;
    }
    const results = matching.map((e) => entryGap(e, checks, now));
    if (results.some((r) => r.reviewed)) continue;
    const best = results
      .filter((r): r is Extract<typeof r, { reviewed: false }> => !r.reviewed)
      .sort((a, b) => REASON_RANK[a.reason] - REASON_RANK[b.reason])[0];
    gaps.push({ ...where, reason: best.reason, detail: best.detail });
  }

  return {
    eligible: gaps.length === 0,
    mode: 'required_certifications',
    requiredCertifications: requirements.map(displayCertificationName),
    requiredCertificationLabels: requirements.map((r) => describeCertificationRequirement(r, ctx)),
    reviewedCertifications,
    gaps,
  };
}

/**
 * One credential the job lists (required or preferred), as the candidate stands with it under the
 * counting rule: 'reviewed' (counts), 'expired', 'rejected', 'pending' (on the profile, document not
 * reviewed yet: checking, no document, check unavailable) or 'missing' (not on the profile).
 */
export type JobCredentialState = 'reviewed' | 'expired' | 'rejected' | 'pending' | 'missing';

export function credentialStateForJob(params: {
  profile: unknown;
  job: Parameters<typeof getJobCertRequirements>[0] | null | undefined;
  certification: string;
  now?: Date;
}): JobCredentialState {
  const checks = (params.profile as { certificationChecks?: unknown } | null | undefined)?.certificationChecks;
  const now = scoringClock(params.profile, params.now ?? new Date());
  const ctx = params.job ? certificationJobContext(params.job) : null;
  const matching = profileCertEntries(params.profile).filter((e) => certificationSatisfiesForJob(e.name, e.state, params.certification, ctx, e.level));
  if (!matching.length) return 'missing';
  const results = matching.map((e) => entryGap(e, checks, now));
  if (results.some((r) => r.reviewed)) return 'reviewed';
  const reasons = results.map((r) => (r.reviewed ? null : r.reason));
  if (reasons.includes('expired')) return 'expired';
  if (reasons.includes('rejected')) return 'rejected';
  return 'pending';
}

const REASON_TEXT: Record<DocumentReviewGapReason, string> = {
  not_on_profile: 'not on your profile yet',
  expired: 'expired: upload your renewed card',
  no_document: 'no document uploaded',
  checking: 'document is still being checked',
  rejected: 'document was rejected',
  check_unavailable: "the document check couldn't run: upload it again",
  not_checked: "document hasn't been checked: upload it again",
};

export function describeDocumentReviewGap(gap: DocumentReviewGap): string {
  return REASON_TEXT[gap.reason];
}

/** One-line explanation used as the server error message and the client toast. */
export function describeDocumentReviewGate(gate: DocumentReviewGate): string {
  if (gate.eligible) return '';
  const parts = gate.gaps.map((g) => `${g.label || g.certification} (${describeDocumentReviewGap(g)})`);
  return `${DOCUMENTS_REVIEWED_REQUIRED_PREFIX} for: ${parts.join('; ')}. Upload a document for each required credential that passes our automated checks.`;
}
